/**
 * "Interaction takes priority over animation": a short window during which animated consumers back
 * off so the main thread can do something the user is waiting for.
 *
 * WHY THIS EXISTS. An animating page keeps the main thread busy every frame (playhead → GL layers,
 * boat canvas, wind particles, tables, uPlot). A page load competing with that used to lose outright:
 * clicking another live page did nothing until the user paused the map by hand. This automates the
 * pause — briefly, and without stopping the clock.
 *
 * A DEADLINE, NOT A FLAG. A boolean "busy" flag freezes the animation forever if anything forgets to
 * clear it (an early return, a thrown error, an abandoned load). A timestamp cannot get stuck: it
 * expires on its own, and `MAX_YIELD_MS` caps how far into the future any caller can push it. The
 * worst case for a buggy caller is a stutter, never a frozen map.
 *
 * NOT a Solid signal, on purpose: every consumer already runs inside a per-frame loop and re-reads
 * this next frame. Making it reactive would add a reactive cascade to the very thing being quietened.
 */

/** Default window: long enough to cover a component load plus its first render. */
const DEFAULT_YIELD_MS = 1200;
/** Ceiling on how far ahead any single call can push the deadline. */
export const MAX_YIELD_MS = 3000;

let yieldUntilMs = 0;

/**
 * Ask animated consumers to back off for `ms` (default ~1.2s). Extends an open window rather than
 * shortening it, so overlapping callers cooperate; clamped to `MAX_YIELD_MS` from now.
 */
export function yieldRenderFor(ms: number = DEFAULT_YIELD_MS): void {
  const now = Date.now();
  const requested = now + Math.max(0, Math.min(ms, MAX_YIELD_MS));
  const ceiling = now + MAX_YIELD_MS;
  yieldUntilMs = Math.min(Math.max(yieldUntilMs, requested), ceiling);
}

/** True while a yield window is open. Cheap enough to call every frame. */
export function isRenderYieldActive(): boolean {
  return Date.now() < yieldUntilMs;
}

/** End the window immediately (tests, and anywhere that knows the wait is over). */
export function clearRenderYield(): void {
  yieldUntilMs = 0;
}
